import { usePermission } from '@/app/usePermission';
import { PERMISSION } from '@/app/permissions';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useStatusLine } from '@/features/common/useStatusLine';
import { StatusLine } from '@/features/common/StatusLine';
import { useSession } from '@/app/session';
import { Icon } from '@/features/common/Icon';
import { communityApi } from '@/features/publicsite/community/api/communityApi';
import type { LoadState } from '@/features/publicsite/types/community/LoadState';
import { forumLoadFailure } from '@/features/publicsite/community/forumLoadFailure';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import type { ForumPost } from '@/features/publicsite/types/community/ForumPost';
import { publicCommunity } from '@/i18n/dictionaries/publicCommunity';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * A topic's thread: approved replies, a composer, and — for a forums.moderate holder — the
 * pending replies a pre-moderated forum is holding back (ToR §6.13's "verificare a conținutului
 * postat"). Pre-moderation happens here, in the thread the post belongs to, rather than on a
 * site-wide queue: a moderator deciding on a reply reads it beside the ones already visible.
 */
export function TopicPage() {
  const { forumId = '', topicId = '' } = useParams();
  const { user } = useSession();
  const { t } = useTranslation(publicCommunity);
  const [posts, setPosts] = useState<ForumPost[] | null>(null);
  const [pending, setPending] = useState<ForumPost[] | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [reply, setReply] = useState('');
  const { status, statusKind, succeed } = useStatusLine();

  const isModerator = usePermission(PERMISSION.forumsModerate);

  // Imperative re-fetches after a mutation (reply submitted, a pending post decided) — not
  // effect dependencies, since nothing but the button handlers below ever calls them.
  const refreshPosts = () => communityApi.posts(topicId).then(setPosts);
  const refreshPending = () => {
    if (!isModerator) return;
    communityApi
      .pendingPosts(topicId)
      .then(setPending)
      .catch(() => setPending([]));
  };

  useEffect(() => {
    let live = true;
    setState('loading');
    communityApi
      .posts(topicId)
      .then((p) => {
        if (!live) return;
        setPosts(p);
        setState('ok');
      })
      .catch((e) => {
        if (!live) return;
        setState(forumLoadFailure(e));
      });
    return () => {
      live = false;
    };
  }, [topicId]);

  useEffect(() => {
    if (state !== 'ok' || !isModerator) {
      setPending(null);
      return;
    }
    let live = true;
    communityApi
      .pendingPosts(topicId)
      .then((p) => {
        if (live) setPending(p);
      })
      .catch(() => {
        if (live) setPending([]);
      });
    return () => {
      live = false;
    };
  }, [topicId, state, isModerator]);

  usePublicPageTitle(
    state === 'missing'
      ? t('notFound')
      : state === 'forbidden'
        ? t('privateTitle')
        : t('topicHeading'),
  );

  const submitReply = async () => {
    if (!reply.trim()) return;
    await communityApi.reply(topicId, reply.trim());
    setReply('');
    succeed(t('replySubmitted'));
    refreshPosts();
    refreshPending();
  };

  const decide = async (postId: string, approve: boolean) => {
    await communityApi.moderatePost(postId, approve);
    refreshPosts();
    refreshPending();
  };

  if (state === 'loading') {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }
  if (state === 'forbidden') {
    return (
      <section className="container section">
        <h1>{t('privateTitle')}</h1>
        <p>{t('privateBody')}</p>
        <p>
          <Link to={`/comunitate/forumuri/${forumId}`}>{t('backToForum')}</Link>
        </p>
      </section>
    );
  }
  if (state === 'missing') {
    return (
      <section className="container section">
        <h1>{t('notFound')}</h1>
        <p>
          <Link to="/comunitate">{t('backToCommunity')}</Link>
        </p>
      </section>
    );
  }

  return (
    <section className="container section">
      <Link className="detail-back" to={`/comunitate/forumuri/${forumId}`}>
        <Icon name="chevron-left" className="detail-back-icon" />
        {t('backToForum')}
      </Link>
      <h1>{t('topicHeading')}</h1>
      <StatusLine status={status} kind={statusKind} />

      {posts && posts.length === 0 && <p className="empty-state">{t('noPosts')}</p>}
      {posts && posts.length > 0 && (
        <div className="doc-list workqueue-list">
          {posts.map((p) => (
            <div className="doc-row" key={p.id}>
              <div className="doc-row__left">
                <div className="doc-row__title topic-post__body">{p.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {user ? (
        <p className="reply-form reply-form--top reply-form--inset-lg">
          <textarea
            className="reply-form__input"
            rows={3}
            placeholder={t('replyPlaceholder')}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <button className="btn btn--primary" onClick={() => void submitReply()}>
            {t('replyAction')}
          </button>
        </p>
      ) : (
        <p>
          <a className="btn btn--primary btn--md" href="/saml/authenticate/mpass">
            <img className="btn__logo" src="/img/gov/mpass.svg" alt="" aria-hidden="true" />
            <span>{t('signInToParticipate')}</span>
          </a>
        </p>
      )}

      {isModerator && (
        <>
          <h2>{t('pendingHeading')}</h2>
          {(!pending || pending.length === 0) && <p className="empty-state">{t('noPending')}</p>}
          {pending && pending.length > 0 && (
            <div className="doc-list workqueue-list">
              {pending.map((p) => (
                <div className="doc-row" key={p.id}>
                  <div className="doc-row__left">
                    <div className="doc-row__title topic-post__body">{p.body}</div>
                  </div>
                  <div className="doc-row__actions">
                    <button
                      className="btn btn--primary workqueue-action workqueue-action--approve"
                      onClick={() => void decide(p.id, true)}
                    >
                      {t('approve')}
                    </button>
                    <button
                      className="btn btn--danger workqueue-action workqueue-action--reject"
                      onClick={() => void decide(p.id, false)}
                    >
                      {t('reject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
